import { useCallback } from 'react'
import { useSetAtom, useAtom } from 'jotai'
import { useDispatch } from 'react-redux'
import { repopulateBasket } from '@mindfulchefuk/actions/basketActions'
import {
  Recipe,
  RecipeWithQuantity,
  TSortByFilter,
} from '@mindfulchefuk/features/Recipes/interfaces'
import { THandleIncreaseOrDecrement } from '@mindfulchefuk/onboarding/types'
import {
  logAddRecipeToCart,
  logRemoveRecipeFromCart,
} from '@mindfulchefuk/utils/analytics'
import { trackAnalyticsEvent } from '@mindfulchefuk/utils/analytics/amplitude/amplitudeAdapter'
import { DROPDOWN_CLOSING_DELAY } from '@mindfulchefuk/constants'
import { PortionCount } from '@mindfulchefuk/config/portionCountConfig'
import mealPlanConfig, { TMealPlan } from '@mindfulchefuk/config/mealPlanConfig'
import { RecipeSortId } from '@mindfulchefuk/config/sortOptionsConfig'
import {
  logDeliveryDateChange,
  logMealPlanChange,
  logPortionCountChange,
  logRecipesSortChange,
} from '@mindfulchefuk/features/Onboarding/utils/onboardingAnalytics'
import { dateFormats } from '@mindfulchefuk/lib/dateFormats'
import { logOnboardingIncrementRecipeFromBasket } from '@mindfulchefuk/utils/analytics/ga/onboardingRecipeBasket'
import {
  addRecipeToBasketAtom,
  removeRecipeFromBasketAtom,
  updateChooseRecipeFiltersAtom,
  updateChooseRecipeSortingAtom,
} from '@mindfulchefuk/store/onboarding'
import { postOnboardingPreview } from '@mindfulchefuk/onboarding/services/postOnboardingPreview'

export type TUseChooseRecipesContextActionsValues = {
  handleAddRecipe: ({
    recipe,
    clickOrigin,
  }: THandleIncreaseOrDecrement<Recipe | RecipeWithQuantity>) => void
  handleRemoveRecipe: ({
    recipe,
    clickOrigin,
  }: THandleIncreaseOrDecrement<Recipe | RecipeWithQuantity>) => void
  handleUpdateDate: (date: Date) => void
  handleUpdateMealPlan: (mealPlan: TMealPlan) => void
  handleUpdatePortionCount: (portionCount: PortionCount) => void
  handleUpdateSortBy: (sortBy: RecipeSortId) => void
  toggleFilter: (filter: TSortByFilter) => void
}

export const useChooseRecipesContextActions = ({
  expandedRecipeFilter,
  selectedMealPlan,
  selectedPortionCount,
  selectedSortBy,
  setExpandedRecipeFilter,
}: {
  expandedRecipeFilter: TSortByFilter | null
  selectedMealPlan: TMealPlan
  selectedPortionCount: PortionCount
  selectedSortBy: RecipeSortId
  setExpandedRecipeFilter: (filter: TSortByFilter | null) => void
}): TUseChooseRecipesContextActionsValues => {
  const updateChooseRecipeFilters = useSetAtom(updateChooseRecipeFiltersAtom)
  const updateRecipeSorting = useSetAtom(updateChooseRecipeSortingAtom)
  const addRecipeToBasket = useSetAtom(addRecipeToBasketAtom)
  const removeRecipeFromBasket = useSetAtom(removeRecipeFromBasketAtom)
  const [{ mutate: handleUpdateOnboardingPreview }] = useAtom(
    postOnboardingPreview
  )
  const dispatch = useDispatch()

  const handleCloseRecipeFilter = useCallback(
    () => setTimeout(setExpandedRecipeFilter, DROPDOWN_CLOSING_DELAY, null),
    [setExpandedRecipeFilter]
  )

  const toggleFilter = useCallback(
    (filter: TSortByFilter) => {
      if (expandedRecipeFilter !== filter) {
        trackAnalyticsEvent({
          name: 'expand filter / sort',
          data: {
            'filter type': filter,
            'page source': 'choose recipes onboarding',
          },
        })

        setExpandedRecipeFilter(filter)
      } else {
        handleCloseRecipeFilter()
      }
    },
    [expandedRecipeFilter, handleCloseRecipeFilter, setExpandedRecipeFilter]
  )

  const recipeFilterTracking = useCallback(
    ({
      portionCount = selectedPortionCount,
      sortBy = selectedSortBy,
      mealPlan = selectedMealPlan as TMealPlan,
    }: {
      portionCount?: PortionCount
      sortBy?: RecipeSortId
      mealPlan?: TMealPlan
    }) =>
      trackAnalyticsEvent({
        name: 'filter / sort by',
        data: {
          'recipe portion count': portionCount,
          'sort selected': sortBy,
          'meal plan selected': mealPlan,
          'page source': 'choose recipes onboarding',
        },
      }),
    [selectedPortionCount, selectedSortBy, selectedMealPlan]
  )

  const handleUpdateMealPlan = useCallback(
    (newMealPlan: TMealPlan) => {
      const { excludedFoodGroups: mealPlanExcludedFoodGroups } =
        mealPlanConfig.find((mealPlan) => mealPlan.value === newMealPlan)
      logMealPlanChange(newMealPlan)
      updateChooseRecipeFilters({
        mealPlan: newMealPlan,
        excludedFoodGroups: mealPlanExcludedFoodGroups,
      })
      recipeFilterTracking({ mealPlan: newMealPlan })
      handleCloseRecipeFilter()
      dispatch(repopulateBasket())
    },
    [
      dispatch,
      recipeFilterTracking,
      handleCloseRecipeFilter,
      updateChooseRecipeFilters,
    ]
  )

  const handleUpdateDate = useCallback(
    (newDate: Date) => {
      const calendarDate = dateFormats.iso(newDate)
      recipeFilterTracking({})
      updateChooseRecipeFilters({ deliveryDate: calendarDate })
      logDeliveryDateChange()
      handleCloseRecipeFilter()
      dispatch(repopulateBasket())
    },
    [
      dispatch,
      recipeFilterTracking,
      handleCloseRecipeFilter,
      updateChooseRecipeFilters,
    ]
  )

  const handleUpdatePortionCount = useCallback(
    (newPortionCount: PortionCount) => {
      logPortionCountChange(newPortionCount)
      updateChooseRecipeFilters({ portionCount: newPortionCount })
      recipeFilterTracking({ portionCount: newPortionCount })
      handleCloseRecipeFilter()
      dispatch(repopulateBasket())
    },
    [
      dispatch,
      recipeFilterTracking,
      handleCloseRecipeFilter,
      updateChooseRecipeFilters,
    ]
  )

  const handleUpdateSortBy = useCallback(
    (newSortBy: RecipeSortId) => {
      updateRecipeSorting(newSortBy)
      logRecipesSortChange(newSortBy)
      recipeFilterTracking({ sortBy: newSortBy })
      handleCloseRecipeFilter()
    },
    [handleCloseRecipeFilter, recipeFilterTracking, updateRecipeSorting]
  )

  const handleAddRecipe = useCallback(
    ({
      clickOrigin,
      recipe,
    }: THandleIncreaseOrDecrement<Recipe | RecipeWithQuantity>) => {
      addRecipeToBasket(recipe.id)
      handleUpdateOnboardingPreview()
      logAddRecipeToCart(recipe)
      if (clickOrigin === 'basket') {
        logOnboardingIncrementRecipeFromBasket()
      }

      trackAnalyticsEvent({
        name: 'product added',
        data: {
          'product id': recipe.id,
          'product name': recipe.title,
          'product type': 'recipe',
          'product category': 'recipes',
          'product sub-category': recipe.cuisine,
          'product price': recipe.price,
          'recipe portion count': recipe.portions,
          'product quantity': 1,
          'page source': clickOrigin === 'modal' ? 'recipes details' : null,
          'food group': recipe.foodGroup,
          rawPageSource: sessionStorage.getItem('referrerPath'),
        },
      })
    },
    [addRecipeToBasket, handleUpdateOnboardingPreview]
  )

  const handleRemoveRecipe = useCallback(
    ({
      clickOrigin,
      recipe,
    }: THandleIncreaseOrDecrement<Recipe | RecipeWithQuantity>) => {
      removeRecipeFromBasket(recipe.id)
      handleUpdateOnboardingPreview()
      trackAnalyticsEvent({
        name: 'product removed',
        data: {
          'product id': recipe.id,
          'product name': recipe.title,
          'product type': 'recipe',
          'product category': 'recipes',
          'product sub-category': recipe.cuisine,
          'product price': recipe.price,
          'recipe portion count': recipe.portions,
          'product quantity': 1,
          'page source': clickOrigin === 'modal' ? 'recipes details' : null,
          'food group': recipe.foodGroup,
          rawPageSource: sessionStorage.getItem('referrerPath'),
        },
      })
      logRemoveRecipeFromCart(recipe)
    },
    [removeRecipeFromBasket, handleUpdateOnboardingPreview]
  )

  return {
    handleAddRecipe,
    handleRemoveRecipe,
    handleUpdateDate,
    handleUpdateMealPlan,
    handleUpdatePortionCount,
    handleUpdateSortBy,
    toggleFilter,
  }
}
